import { Button, CircleButton, Input, Select, Toggle, TooltipWrapper } from '@ui';
import useTranslation from '@app/hooks/use-translation';
import { AllStatusProject } from '@app/interfaces/analytics';
import {
  MetricObjType,
  MetricTypesArrType,
} from '@app/interfaces/pages-types/anatylics-metric.type';
import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import { Controller, useForm } from 'react-hook-form';
import {
  useCreateProjectMetricGroupMutation,
  useEditProjectMetricGroupMutation,
} from '@app/store/api/metrics.api';
import {
  useCreateProjectChatMetricGroupMutation,
  useEditProjectChatMetricGroupMutation,
} from '@app/store/api/chat-api/chat-metric.api';
import { usePermissionCheck } from '@app/hooks/use-permission';
import { userPermissions } from '@app/utils/user-permissions';
import { AliasSettingsObj } from '@app/interfaces/settings.type';
type FormType = {
  name: string;
  alias_id: string;
  export_result?: boolean;
  dnt_display?: boolean;
  export_id?: string;
};
const MetaMetricModal: React.FC<{
  metric: MetricTypesArrType;
  editModal?: boolean;
  copyModal?: boolean;
  modalsSetState: (type: string) => void;
  status?: AllStatusProject | undefined;
  unitVal?: string;
  metricSettings?: MetricObjType | undefined;
  currentTemplate: 'chat' | 'voice';
  aliases: AliasSettingsObj[];
}> = ({
  metric,
  editModal,
  modalsSetState,
  status,
  metricSettings,
  currentTemplate,
  aliases,
  copyModal,
}) => {
  const { id } = useParams();
  const { t } = useTranslation('pages.metricTemplates');
  const { t: tf } = useTranslation('common.forms');
  const isNotAdmin = usePermissionCheck({
    permissions: 'display',
    tag: userPermissions.display.displayUsers,
  });
  // const [, { data: metricSettings }] = useLazyGetProjectMetricQuery();
  const [createMetricChat] = useCreateProjectChatMetricGroupMutation();
  const [editMetricChat] = useEditProjectChatMetricGroupMutation();
  const [createMetricVoice] = useCreateProjectMetricGroupMutation();
  const [editMetricVoice] = useEditProjectMetricGroupMutation();
  const createMetric = { chat: createMetricChat, voice: createMetricVoice };
  const editMetric = { chat: editMetricChat, voice: editMetricVoice };
  const defaultValues: FormType = useMemo(
    () =>
      editModal || copyModal
        ? {
            name: `${metricSettings?.name || ''} ${copyModal ? t('copy') : ''}`.trim(),
            alias_id: metricSettings?.settings?.alias_id || 'default',
            export_result: metricSettings?.export?.export_result,
            export_id: metricSettings?.export?.export_id,
            dnt_display: metricSettings?.dnt_display,
          }
        : { name: '', alias_id: 'default', export_result: false },
    [
      copyModal,
      editModal,
      metricSettings?.dnt_display,
      metricSettings?.export?.export_id,
      metricSettings?.export?.export_result,
      metricSettings?.name,
      metricSettings?.settings?.alias_id,
      t,
    ],
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = useForm<FormType>({
    defaultValues: defaultValues,
  });
  const disableFunc = () => {
    if (status === AllStatusProject.PAUSE) {
      return false;
    }
    return status !== AllStatusProject.CREATED;
  };
  const copyToClipboard = useCallback(() => {
    event?.preventDefault();
    const textToCopy = metricSettings?.metric_id;
    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy);
    }
  }, [metricSettings?.metric_id]);

  const closeModal = () => {
    modalsSetState(metric.type);
  };

  const getResultValueType = (id: string) => {
    return aliases?.find((alias) => alias.alias_id === id)?.type_value;
  };

  const onSubmit = (data: FormType) => {
    const obj: MetricObjType = {
      name: data.name,
      dnt_display: data.dnt_display,
      description: ' ',
      type: metric.type,
      result_value_type: getResultValueType(watch('alias_id')),
      export: {
        export_result: data.export_result,
        export_id: data.export_result ? data.export_id : undefined,
      },
      settings: {
        alias_id: data.alias_id,
      },
    };
    if (!editModal) {
      id && createMetric[currentTemplate || 'voice']({ params: { project_id: id }, body: obj });
    } else {
      if (id && metricSettings?.metric_id) {
        editMetric[currentTemplate || 'voice']({
          params: { project_id: id, metric_id: metricSettings.metric_id },
          body: obj,
        });
      }
    }
    closeModal();
  };
  const selectSetter = (value: string | number) => {
    setValue('name', aliases?.find((alias) => alias.alias_id === value)?.name as string);
    setValue('alias_id', value as string);
    errors.alias_id?.message && clearErrors();
  };
  const getSelectItems = () => {
    return (
      aliases
        ?.filter((e) => e.type_meta !== 'system')
        .map((e) => {
          return { title: e.name, value: e.alias_id };
        }) || []
    );
  };
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="min-w-[550px] m-[0px_auto] px-[50px]">
        <div className="flex flex-col gap-[20px]">
          <div className="flex items-center justify-between">
            <span className="text-0color text-[15px]">{t('metric_group')}</span>
            <span className="mr-[168px]">
              <b>
                {metric.group.toUpperCase()} {t('one_metric')}
              </b>
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-0color text-[15px]">{t('alias')}</span>
            <div className="w-[280px]">
              <Controller
                render={() => (
                  <Select
                    withSearch
                    dropWidth={276}
                    dropHeight={130}
                    defaultValue={watch('alias_id')}
                    error={errors.alias_id?.message}
                    options={[
                      ...getSelectItems(),
                      {
                        title: t('choose') + '...',
                        value: 'default',
                        disabled: true,
                        nullable: true,
                      },
                    ]}
                    onChange={selectSetter}
                  />
                )}
                name={'alias_id'}
                rules={{ validate: (value) => value !== 'default' || tf('required') }}
                control={control}
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-0color text-[15px]">{t('metric_name')}</span>
            <div className="w-[280px]">
              <Input
                error={errors.name?.message}
                {...register('name', { required: { value: true, message: tf('required') } })}
                name={'name'}
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-0color text-[15px]">{t('dnt_display_in_record')}</span>
            <div className="w-[280px] items-end flex justify-end">
              <Toggle
                checked={watch('dnt_display')}
                onChecked={(val) => setValue('dnt_display', val)}
              />
            </div>
          </div>
          {editModal && (
            <div className="flex items-center justify-between">
              <span className="text-0color text-[15px]">{t('metric_id')}</span>
              <div className="flex gap-1">
                <p>{metricSettings?.metric_id}</p>
                <TooltipWrapper content={t('popup_hints.copy_id')} id={'meta_metrick_copy'}>
                  <CircleButton
                    onClick={copyToClipboard}
                    icon="CopyIcon"
                    className="text-action mb-[5px]"
                    size={16}
                  />
                </TooltipWrapper>
              </div>
            </div>
          )}
          {!isNotAdmin && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-0color text-[15px]">{t('export_result')}</span>
                <div className="w-[280px] items-end flex justify-end">
                  <Toggle
                    checked={watch('export_result')}
                    onChecked={(val) => setValue('export_result', val)}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-0color text-[15px]">{t('export_id')}</span>
                <div className="w-[280px] ">
                  <Input
                    {...register('export_id', {
                      required: { value: !!watch('export_result'), message: tf('required') },
                    })}
                    error={errors.export_id?.message}
                    disabled={!watch('export_result')}
                  />
                </div>
              </div>
            </>
          )}
          <div className="sticky bottom-0">
            <div className="bg-[#fff]">
              <div className="flex items-center gap-[15px] pt-4 pb-[40px]">
                <Button type="submit" disabled={disableFunc()} label={t('save')} />
                <Button onClick={() => closeModal()} fill="linked" label={t('cancel')} />
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};
export default MetaMetricModal;
